import React, { useCallback, useMemo, useState } from 'react';
import { Trans } from '@lingui/macro';
import PanelContent from 'components/base/panel/panelcontent';
import ActionBlock from 'components/base/actionblock/actionblock';
import { Reading } from 'util/backendapi/types/Model';
import Button, { ButtonPrimary } from 'components/base/button/button';
import { formatDatetimeForDisplay } from 'util/dates';
import { Field, Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import { FieldError } from 'components/base/form/errornotice/errornotice';
import { FormItem } from 'components/base/form/FormItem';
import { menuItemsFromEnum } from 'components/base/i18n/menuItemsFromEnum';
import { Media_CONTENT_TYPE } from 'util/backendapi/types/Enum';
import { SimpleSelectOption } from 'components/base/form/simpleselect/simpleselect';
import { SimpleSelectField } from 'components/base/form/simpleselect/simpleselectfield';
import {
  MultiFileField,
  MultiFileFormValue,
} from 'components/base/form/multi-file-field/MultiFileField';
import './UploadMediaToReadingPanelView.scss';
import {
  MediaToUpload,
  MediaUploadProgressModal,
} from 'screens/media/list/MediaUploadProgressModal';
import { useIsMounted } from 'util/hooks';
import { getApi } from 'util/backendapi/fetch';
import { showErrorsInFormik } from 'util/backendapi/error-formik';
import { getExpectedFields } from 'util/backendapi/error';

export interface UploadMediaToReadingFormValues {
  description: string;
  content_type: Media_CONTENT_TYPE | null;
  attached_files: MultiFileFormValue;
}

export interface UploadMediaToReadingViewProps {
  reading: Reading;
  timeZone: string | null | undefined;
  onAfterSubmit: () => void;
  onCancel: () => void;
}

export function UploadMediaToReadingPanelView(
  props: UploadMediaToReadingViewProps
) {
  const { reading, timeZone, onAfterSubmit, onCancel } = props;

  const isMounted = useIsMounted();
  const [toUpload, setToUpload] = useState<MediaToUpload[]>();

  const initialValues: UploadMediaToReadingFormValues = useMemo(
    () => ({
      attached_files: [],
      content_type: null,
      description: '',
    }),
    []
  );

  const contentTypeOptions: SimpleSelectOption<Media_CONTENT_TYPE>[] =
    React.useMemo(
      () =>
        menuItemsFromEnum('Media_CONTENT_TYPE', [
          Media_CONTENT_TYPE.ENGINEERING_INSPECTION,
          Media_CONTENT_TYPE.ROUTE_MARCH_PHOTO,
          Media_CONTENT_TYPE.SAFETY_REPORT,
        ]),
      []
    );

  const onSubmit = useCallback(
    async (
      values: UploadMediaToReadingFormValues,
      formik: FormikHelpers<UploadMediaToReadingFormValues>
    ) => {
      try {
        const observationPoint = await getApi(
          `/observation-points/${reading.observation_point.id}/`
        );
        setToUpload(
          values.attached_files.map(
            (f): MediaToUpload => ({
              content_type: values.content_type!,
              description: values.description,
              readings: [reading.id],
              observation_points: [observationPoint.id],
              sites: [observationPoint.site.id],
              areas: [observationPoint.area.id],
              attached_file: f.file!,
            })
          )
        );
      } catch (e) {
        if (isMounted()) {
          formik.setSubmitting(false);
          showErrorsInFormik(formik, e, getExpectedFields(values));
        }
      }
    },
    [reading, isMounted]
  );

  const validate = useCallback((values: UploadMediaToReadingFormValues) => {
    let errors: FormikErrors<UploadMediaToReadingFormValues> = {};

    if (values.attached_files.length === 0) {
      errors.attached_files = (<Trans>A file is required.</Trans>) as any;
    }

    if (!values.content_type) {
      errors.content_type = (<Trans>Content type is required</Trans>) as any;
    }

    return errors;
  }, []);

  const panelHeader = <Trans>Upload media to reading</Trans>;

  if (toUpload) {
    return (
      <PanelContent header={panelHeader}>
        <MediaUploadProgressModal
          itemsToUpload={toUpload}
          onClose={onAfterSubmit}
        />
      </PanelContent>
    );
  }

  return (
    <PanelContent header={panelHeader}>
      Upload media to reading for{' '}
      <strong>{reading.observation_point.code}</strong> at{' '}
      <strong>
        {formatDatetimeForDisplay(reading.reading_datetime, timeZone)}
      </strong>
      .
      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, status }) => (
          <Form>
            {status}
            <FormItem label={<Trans>Select a file</Trans>}>
              <MultiFileField name="attached_files" />
              <FieldError name="attached_files" />
            </FormItem>

            <FormItem label={<Trans>Description</Trans>}>
              <Field
                className="upload-media-to-reading-description"
                component="textarea"
                name="description"
                rows="2"
              />
              <FieldError name="description" />
            </FormItem>

            <FormItem label={<Trans>Content type</Trans>}>
              <SimpleSelectField
                name="content_type"
                options={contentTypeOptions}
                placeholder={<Trans>Select a type of content</Trans>}
              />
              <FieldError name="content_type" />
            </FormItem>

            <ActionBlock>
              <Button onClick={onCancel}>
                <Trans>Cancel</Trans>
              </Button>
              <ButtonPrimary
                type="submit"
                disabled={isSubmitting}
                iconType="icon-save"
              >
                <Trans>Upload</Trans>
              </ButtonPrimary>
            </ActionBlock>
          </Form>
        )}
      </Formik>
    </PanelContent>
  );
}
